import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface SiteCopyRow {
  id: string;
  page: string;
  section: string;
  key: string;
  value: string;
  updated_at: string;
  updated_by: string | null;
}

export function useSiteCopy() {
  const { data: copyData, isLoading } = useQuery({
    queryKey: ['site-copy'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_copy')
        .select('*');
      if (error) throw error;
      return data as SiteCopyRow[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const copyMap = new Map<string, string>();
  copyData?.forEach((row) => copyMap.set(row.key, row.value));

  const getCopy = (key: string, fallback: string): string => {
    return copyMap.get(key) ?? fallback;
  };

  return { getCopy, isLoading, copyData };
}

export function useSiteCopyAdmin() {
  const queryClient = useQueryClient();

  const { data: copyData, isLoading } = useQuery({
    queryKey: ['site-copy'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_copy')
        .select('*')
        .order('page')
        .order('section')
        .order('key');
      if (error) throw error;
      return data as SiteCopyRow[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const updateMutation = useMutation({
    mutationFn: async (changes: { key: string; value: string }[]) => {
      for (const change of changes) {
        const { error } = await supabase
          .from('site_copy')
          .update({ value: change.value })
          .eq('key', change.key);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-copy'] });
    },
  });

  return { copyData, isLoading, updateMutation };
}
